import { Drawing, Pixels } from "./index";
import { Coordinates, toCoords, toIdx } from "./cartesian";

export function crosshairPath(idx: number): Pixels {
  const crosshair: Pixels = {
    [idx]: true,
  };
  // Go up.
  let curr = idx;
  while ((curr -= Drawing.side) >= 0) {
    crosshair[curr] = true;
  }
  // Go down.
  curr = idx;
  while ((curr += Drawing.side) < Drawing.side * Drawing.side) {
    crosshair[curr] = true;
  }
  // Go right.
  curr = idx;
  while (++curr % Drawing.side !== 0) {
    crosshair[curr] = true;
  }
  // Go left.
  curr = idx;
  while (curr-- % Drawing.side !== 0) {
    crosshair[curr] = true;
  }
  return crosshair;
}

export function floodPath(drawing: Drawing, idx: number): Pixels {
  const collector: Pixels = {};
  findFloodPath(idx, drawing.pixels, !drawing.pixels[idx], collector);
  for (const idx in collector) {
    if (collector[idx]) continue;
    delete collector[idx];
  }
  return collector;
}

function findFloodPath(
  idx: number,
  pixels: Pixels,
  floodColor: boolean,
  collector: Pixels
): void {
  // Base case: we already visited this pixel.
  if (collector[idx] !== undefined) return;
  // Base case: this pixel should not be flooded.
  if (!!pixels[idx] === floodColor) {
    collector[idx] = false;
    return;
  }

  // Add pixel to the flood path.
  collector[idx] = true;

  // Go up.
  let next = idx - Drawing.side;
  if (next >= 0) {
    findFloodPath(next, pixels, floodColor, collector);
  }
  // Go down.
  next = idx + Drawing.side;
  if (next < Drawing.side * Drawing.side) {
    findFloodPath(next, pixels, floodColor, collector);
  }
  // Go right.
  next = idx + 1;
  if (next % Drawing.side !== 0) {
    findFloodPath(next, pixels, floodColor, collector);
  }
  // Go west.
  if (idx % Drawing.side !== 0) {
    findFloodPath(idx - 1, pixels, floodColor, collector);
  }
}

/**
 * Returns the straight line path between `start` and `end` according to
 * Bresenham's line algorithm.
 *
 * https://en.m.wikipedia.org/wiki/Bresenham%27s_line_algorithm
 * http://members.chello.at/~easyfilter/bresenham.html
 */
export function straightLinePath(
  drawing: Drawing,
  start: number,
  end: number
): Pixels {
  const startCoords = toCoords(
    Math.floor(start / Drawing.side),
    start % Drawing.side
  );
  const endCoords = toCoords(
    Math.floor(end / Drawing.side),
    end % Drawing.side
  );
  const lineCrossesYAxis = startCoords.x * endCoords.x < 0;
  const lineCrossesXAxis = startCoords.y * endCoords.y < 0;

  let x0 = 0;
  let y0 = 0;
  let x1 = endCoords.x - startCoords.x;
  let y1 = endCoords.y - startCoords.y;
  // Bresenham's algorithm assumes a continuous coord-plane. Drawings have a
  // disjointed coord-plane with holes at x = 0 and y = 0, so we need to account
  // for that here.
  if (lineCrossesYAxis) {
    x1 > x0 ? (x1 -= 1) : (x1 += 1);
  }
  if (lineCrossesXAxis) {
    y1 > y0 ? (y1 -= 1) : (y1 += 1);
  }

  const dx = Math.abs(x1 - x0);
  const sx = x0 < x1 ? 1 : -1;
  const dy = -Math.abs(y1 - y0);
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  let e2;

  const line: Coordinates[] = [];
  while (true) {
    line.push({ x: x0, y: y0 });
    if (x0 === x1 && y0 === y1) break;
    e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }

  const pixels: Pixels = {};
  for (const point of line) {
    // Transform each point to its actual position on the drawing.
    point.x += startCoords.x;
    point.y += startCoords.y;
    if (lineCrossesYAxis) {
      if (endCoords.x > startCoords.x && point.x >= 0) {
        point.x += 1;
      }
      if (endCoords.x < startCoords.x && point.x <= 0) {
        point.x -= 1;
      }
    }
    if (lineCrossesXAxis) {
      if (endCoords.y > startCoords.y && point.y >= 0) {
        point.y += 1;
      }
      if (endCoords.y < startCoords.y && point.y <= 0) {
        point.y -= 1;
      }
    }

    // Only collect the idxs that need flipping.
    const idx = toIdx(point);
    if (idx !== null && drawing.pixels[start] === drawing.pixels[idx]) {
      pixels[idx] = true;
    }
  }

  return pixels;
}
